import { FC, useState } from "react";
import { useAccess } from "../../../hooks/useAccess";
import { IOrder, IOrderItem } from "../../../model/IOrder";
import priceFormatter from "../../../utils/priceFormatter";
import { orderedItems } from "../../../utils/orderTotals";
import { DetailPanel, DetailTable } from "./DetailPanel";
import OrderItemModal from "./OrderItemModal";
import CreateReplacementModal from "../replacement/CreateReplacementModal";
import TrackingProductWidget from "../tracking/TrackingProductWidget";
import TrackingProductsWidget from "../tracking/TrackingProductsWidget";

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : priceFormatter(value);

const day = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-CA');
};

/** "2015 Ford F-150", the parenthetical the old page put after the part name. */
const vehicle = (item: IOrderItem) =>
  [item.viYear, item.viMake, item.viModel].filter((p) => !!p && String(p).trim()).join(' ');

const partscareLine = (item: IOrderItem) => {
  const pc = item.partscare;
  if (!pc) return null;

  if (pc.subscribed)
    return (
      <span className="text-primary">
        PartsCare{pc.coverageYears ? ` ${pc.coverageYears}-Year Coverage` : ''}
        {pc.monthlyAmount != null ? ` (${priceFormatter(pc.monthlyAmount)}/mo)` : ''}
      </span>
    );

  if (pc.eligible)
    return (
      <span className="text-text_pale">
        Offer{pc.monthlyAmount != null ? ` ${priceFormatter(pc.monthlyAmount)}/mo` : ''}
        {pc.offerExpired ? ' (expired)' : ''}
      </span>
    );

  return null;
};

const OrderItems: FC<{ order: IOrder }> = ({ order }) => {
  const { hasRole } = useAccess();
  const items = orderedItems(order);
  const [shown, setShown] = useState<IOrderItem | null>(null);
  const [replacing, setReplacing] = useState<IOrderItem | null>(null);

  // -- Admin only, as on the old page. The endpoints behind the modal are
  // -- ADMIN-only too; this is what keeps the link from being there to click.
  const canReplace = hasRole('ROLE_ADMIN');

  return (
    <DetailPanel title="Items" actions={<TrackingProductsWidget order={order} />}>
      <DetailTable
        head={[
          'Product Name', 'LKQ Stock Number', 'Product ID', 'Supplier ID', 'Invoice',
          'vOrder#', 'Price', 'QTY', 'Shipping Status', 'Shipper', 'Tracking Number', 'PartsCare', '',
        ]}
        empty="This order has no items"
      >
        {items.map((item) => (
          <tr key={item.id} className="odd:bg-gray-50 align-top">
            <td className="py-2 px-2">
              <div className="font-medium">{item.name || '—'}</div>
              {vehicle(item) ? <div className="text-text_pale">(for {vehicle(item)})</div> : null}
              {item.mileage != null ? <div className="text-text_pale">{item.mileage} mi</div> : null}
              {canReplace && (
                <button
                  type="button"
                  className="block text-primary underline underline-offset-2 mt-1"
                  title="Find a part to replace this one and have the store re-create the order"
                  onClick={() => setReplacing(item)}
                >
                  Create Replacement
                </button>
              )}
            </td>
            <td className="py-2 px-2 whitespace-nowrap">{item.stockNumber || '—'}</td>
            <td className="py-2 px-2 whitespace-nowrap">
              {item.pid
                ? (
                  <button
                    type="button"
                    className="text-primary underline underline-offset-2"
                    title="Show the part's description, distance, location and tag"
                    onClick={() => setShown(item)}
                  >
                    {item.pid}
                  </button>
                )
                : '—'}
            </td>
            <td className="py-2 px-2">
              <div className="whitespace-nowrap">{item.supplierId || '—'}</div>
              <div className="text-text_pale whitespace-nowrap">
                {item.vendor?.name || 'LKQ'}
                {item.supplierExternalKey ? ` (${item.supplierExternalKey})` : ''}
              </div>
            </td>
            <td className="py-2 px-2 whitespace-nowrap">{item.supplierInvoice || '—'}</td>
            <td className="py-2 px-2 whitespace-nowrap">{item.salesOrderNumber || '—'}</td>
            <td className="py-2 px-2 text-right whitespace-nowrap">
              <div>Web Price: {money(item.price)}</div>
              <div>vPrice: {money(item.vPrice)}</div>
              <div className="border-t-[1px] border-border_color mt-1 pt-1">
                Shipping (Web): {money(item.actualShippingPrice)}
              </div>
              <div>Shipping Original: {money(item.rawShipping)}</div>
              {item.shippingDiscount != null && item.shippingDiscount !== 0
                ? <div>Shipping Discount: -{priceFormatter(item.shippingDiscount)}</div>
                : null}
            </td>
            <td className="py-2 px-2 text-center">{item.qty ?? 1}</td>
            <td className="py-2 px-2">
              <div className="whitespace-nowrap">{item.itemStatus || '—'}</div>
              {day(item.siDate) ? <div className="text-text_pale whitespace-nowrap">Date: {day(item.siDate)}</div> : null}
            </td>
            <td className="py-2 px-2 whitespace-nowrap">{item.siCarrier || '—'}</td>
            <td className="py-2 px-2 whitespace-nowrap">
              {item.siCarrierLink
                ? <a className="text-primary underline underline-offset-2" href={item.siCarrierLink} target="_blank" rel="noreferrer">{item.siTrackingNum}</a>
                : (item.siTrackingNum || '—')}
            </td>
            <td className="py-2 px-2">{partscareLine(item) || '—'}</td>
            <td className="py-2 px-2">
              <TrackingProductWidget orderItem={item} />
            </td>
          </tr>
        ))}
      </DetailTable>

      {!!shown && <OrderItemModal item={shown} onClose={() => setShown(null)} />}

      {!!replacing && (
        <CreateReplacementModal
          orderId={order.orderId}
          item={replacing}
          onClose={() => setReplacing(null)}
        />
      )}
    </DetailPanel>
  );
};

export default OrderItems;
